import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import TYPE from "../../../constant/TYPE";
import useLaTexCode from "../../../hooks/useLaTexCode";

const AnsweringQuestionsNode = ( props ) => {

  const [ chtml, setCHTML ] = useState( '' );

  const container = useRef();
  const isInitialHighlighting = useRef( false );

  const mathjax = useSelector( state => state.mathjax );
  const answeringQuestions = useSelector( state => state.answeringQuestions );

  const { correctImgCode } = useLaTexCode();

  useEffect( () => {
    if ( mathjax.target ) {
      const correctedImgCode = correctImgCode( props.children );
      const metrics = mathjax.target.getMetricsFor( container.current, true );
      const chtml = mathjax.target.tex2chtml( correctedImgCode, metrics );
      isInitialHighlighting.current = false;
      clearBluelightInAnswerArea();
      clearYellowlightInAnswerArea();
      if ( bluelightingInnerAnswerArea( chtml ) ) {
        isInitialHighlighting.current = true;
        yellowlightingAnswerArea( chtml );
      }
      setCHTML( chtml.outerHTML );
    }
  }, [ mathjax.target, props.children ] );

  useEffect( () => {
    if ( chtml ) {
      if ( ! isInitialHighlighting.current ) {
        isInitialHighlighting.current = true;
        bluelightingOuterAnswerArea();
        yellowlightingAnswerArea();
      }
      mathjax.target.typeset();
      mathjax.target.typesetPromise( [ container.current ] ).then( complete );
    }
  }, [ chtml ] );

  useEffect( () => {
    // console.log('### AnsweringQuestionsNode: ', answeringQuestions.answerIndex);
    if ( isInitialHighlighting.current ) {
      clearYellowlightInAnswerArea();
      yellowlightingAnswerArea();
    }
  }, [ answeringQuestions.answerIndex ] );

  const bluelightingInnerAnswerArea = useCallback( ( chtml ) => {
    let isTarget = false;
    const highlight = ( target ) => {
      const mjxBox = target.closest( 'mjx-box' );
      const answerArea = mjxBox?.nextSibling || mjxBox || target.closest( 'mjx-mtd' );
      if ( answerArea ) {
        isTarget = true;
        answerArea.classList.add( 'active-bd' );
      }
    };
    chtml.querySelectorAll( `[ id*=${ TYPE.ID.ANSWER_AREA } ]` ).forEach( highlight );
    return isTarget;
  }, [] );

  const bluelightingOuterAnswerArea = useCallback( () => {
    const highlight = ( target ) => {
      const answerArea = target.closest( '.numberline-num' ) || target.closest( '.inputTxt' ) || target.closest( '.td_box' );
      answerArea && answerArea.classList.add( 'active-bd' );
    };
    container.current.querySelectorAll( `[ id*=${ TYPE.ID.ANSWER_AREA } ]` ).forEach( highlight );
  }, [] );

  const clearBluelightInAnswerArea = useCallback( () => {
    const clear = ( target ) => {
      target.classList.remove( 'active-bd' );
    };
    const innerAnswerArea = container.current.querySelectorAll( '.active-bd' );
    if ( innerAnswerArea.length ) {
      innerAnswerArea.forEach( clear );
    } else {
      const outerAnswerArea = container.current.closest( '.active-bd' );
      outerAnswerArea && clear( outerAnswerArea );
    }
  }, [] );

  const yellowlightingAnswerArea = useCallback( ( chtml ) => {
    const target = chtml || container.current;
    const innerAnswerArea = target.querySelector( `#${ TYPE.ID.ANSWER_AREA }${ answeringQuestions.answerIndex }` );

    if ( innerAnswerArea ) {
      const answerAreaList = [ innerAnswerArea.closest( '.active-bd' ), innerAnswerArea.closest( 'mjx-box' )?.nextSibling ];
      const answerArea = answerAreaList.filter( v => v );
      answerArea.length && answerArea[ 0 ].classList.add( 'focusedInput' );
    }
  }, [ answeringQuestions.answerIndex ] );

  const clearYellowlightInAnswerArea = useCallback( () => {
    const answerArea = container.current.querySelector( '.focusedInput' ) || container.current.closest( '.focusedInput' );
    answerArea && answerArea.classList.remove( 'focusedInput' );
  }, [] );

  const complete = useCallback( () => {
    props.onRender( container.current, props.index );
  }, [ props.index, props.onRender ] );

  return <div ref={ container } dangerouslySetInnerHTML={ { __html : chtml } } />;

};

AnsweringQuestionsNode.defaultProps = {
  onRender : () => {}
};

export default AnsweringQuestionsNode;
